import { useState } from 'react';
import { TextField, Grid, InputAdornment, IconButton } from '@mui/material';
import type { GridSize } from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { Country } from '../../types/models/Country';
import { SttSelectCountryDialog } from '../dialogs/SttSelectCountryDialog';

type SttSelectCountryProps = {
    grid?: boolean;
    caption: string;
    name: string;
    defVal:
        | {
              id: number;
              name: string;
          }
        | null
        | undefined;
    value: {
        id: number;
        name: string;
    };
    onChange: ({
        name,
        value,
    }: {
        name: string;
        value: Country | null;
    }) => void;
    onlyActive?: boolean;
    xs?: GridSize;
    sm?: GridSize;
    md?: GridSize;
    lg?: GridSize;
    xl?: GridSize;
};

export function SttSelectCountry({
    grid = false,
    caption,
    name,
    defVal,
    value,
    onChange,
    md = 3,
    xs = 12,
    sm,
    lg,
    xl,
}: SttSelectCountryProps) {
    const { t } = useTranslation();

    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const anyCountry = {
        id: 0,
        name: t('country.anyCountry'),
    };

    const defaultValue = defVal ?? anyCountry;

    const dialog = (
        <SttSelectCountryDialog
            open
            onClose={() => {
                setIsDialogOpen(false);
            }}
            onSelect={(country: Country) => {
                setIsDialogOpen(false);
                onChange({ name, value: country });
            }}
        />
    );

    const content = (
        <TextField
            fullWidth
            size="small"
            label={caption}
            variant="outlined"
            value={value?.name || defaultValue.name}
            InputProps={{
                readOnly: true,
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            onClick={() => {
                                onChange({
                                    name,
                                    value: anyCountry,
                                });
                            }}
                        >
                            <ClearIcon />
                        </IconButton>

                        <IconButton
                            onClick={() => {
                                setIsDialogOpen(true);
                            }}
                        >
                            <SearchIcon />
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );

    return grid ? (
        <Grid size={{ xs, sm, md, lg, xl }}>
            {isDialogOpen && dialog}
            {content}
        </Grid>
    ) : (
        <div>
            {isDialogOpen && dialog}
            {content}
        </div>
    );
}
